import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, CircleDot, FileText, Loader2, Mic, NotebookPen, ShieldCheck, Square, X } from 'lucide-react';
import type { CallRecording, Message } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useProjects } from '@/lib/hooks';
import { useCanAnywhere } from '@/lib/permissions';
import { queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { Button, Modal, Select } from '@/components/ui';
import { Bullets } from './CatchUp';
import { clock, dt, fullDateTime, whenWording, ukShortDate } from '@/lib/format';

const RETENTION_NOTE = 'Audio is deleted as soon as it has been transcribed. The transcript is kept for 30 days and only people who agreed to be recorded can read it.';

function useCallRecording(callId: string) {
  return useQuery({ queryKey: ['call-recording', callId], queryFn: () => api.ai.recording(callId), refetchInterval: q => (q.state.data?.recording && ['awaiting_consent', 'recording', 'processing'].includes(q.state.data.recording.status) ? 5000 : 30_000) });
}

/** Strip at the top of the call: request, consent prompt, live indicator and stop. */
export function MeetingNotesBar({ callId }: { callId: string }) {
  const q = useCallRecording(callId);
  const canRecord = useCanAnywhere('ai.meeting_notes.record');
  const toast = useUi(s => s.toast);
  const [busy, setBusy] = useState(false);
  const r = q.data?.recording ?? null;
  const act = async (fn: () => Promise<CallRecording>) => {
    setBusy(true);
    try { const out = await fn(); queryClient.setQueryData(['call-recording', callId], { recording: out, available: q.data?.available ?? true }); } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };
  if (!q.data) return null;
  const live = r && ['awaiting_consent', 'recording', 'processing'].includes(r.status);
  if (!live) {
    if (!q.data.available || !canRecord) return null;
    return <button disabled={busy} onClick={() => void act(() => api.ai.requestRecording(callId))} className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-white/80 hover:bg-white/10" title="Ask everyone to agree to record this call for AI meeting notes"><NotebookPen className="h-3.5 w-3.5" />Meeting notes</button>;
  }
  if ((r.status === 'awaiting_consent' || r.status === 'recording') && r.my_decision !== 'granted') {
    return (
      <div className="fixed left-1/2 top-16 z-[60] w-[30rem] -translate-x-1/2 rounded-xl bg-white p-4 text-slate-900 shadow-2xl ring-1 ring-slate-200" role="alertdialog" aria-label="Recording consent">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 rounded-full bg-rose-50 p-2 text-rose-600 dark:bg-rose-900/40 dark:text-rose-200"><Mic className="h-4 w-4" /></span>
          <div className="min-w-0 flex-1">
            <div className="font-semibold">{r.status === 'recording' ? 'This call is being recorded for meeting notes' : `${r.requested_by_name} would like to record this call for meeting notes`}</div>
            <p className="mt-1 text-sm text-slate-600">{r.status === 'recording' ? 'Recording stops within a minute unless you agree. If you decline, the recording is deleted.' : 'Recording only starts if everyone in the call agrees.'} A summary and action items are posted in the chat afterwards.</p>
            <p className="mt-2 flex gap-1.5 text-xs text-slate-500"><ShieldCheck className="mt-px h-3.5 w-3.5 shrink-0" />{RETENTION_NOTE}</p>
            <div className="mt-3 flex gap-2">
              <Button size="sm" disabled={busy} onClick={() => void act(() => api.ai.consent(r.id, true))}>I agree to be recorded</Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => void act(() => api.ai.consent(r.id, false))}>Don't record</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  const waiting = r.consents.filter(c => c.decision !== 'granted').map(c => c.display_name.split(' ')[0]);
  return (
    <div className="flex items-center gap-2 text-xs">
      {r.status === 'recording' && <span className="flex items-center gap-1.5 rounded-full bg-rose-500/20 px-2 py-0.5 font-medium text-rose-200" data-testid="recording"><CircleDot className="h-3.5 w-3.5 animate-pulse text-rose-400" />Recording for meeting notes</span>}
      {r.status === 'awaiting_consent' && <span className="rounded-full bg-white/10 px-2 py-0.5 text-white/80">Meeting notes: waiting for {waiting.join(', ') || 'everyone'} to agree</span>}
      {r.status === 'processing' && <span className="flex items-center gap-1.5 text-white/70"><Loader2 className="h-3.5 w-3.5 animate-spin" />Writing meeting notes…</span>}
      {r.status !== 'processing' && <button disabled={busy} onClick={() => void act(() => api.ai.stopRecording(r.id))} className="flex items-center gap-1 rounded-md px-2 py-1 text-white/80 hover:bg-white/10">{r.status === 'recording' ? <><Square className="h-3 w-3" />Stop</> : <><X className="h-3 w-3" />Cancel</>}</button>}
    </div>
  );
}

/** The notes post in the channel: summary, action items (→ tasks), transcript for people who were recorded. */
export function MeetingNotesCard({ m }: { m: Message }) {
  const id = String((m.metadata as { recording_id?: string }).recording_id);
  const { userId } = useAuth();
  const q = useQuery({ queryKey: ['recording', id], queryFn: () => api.ai.getRecording(id) });
  const projects = useProjects();
  const toast = useUi(s => s.toast);
  const [taskFor, setTaskFor] = useState<number | null>(null);
  const [project, setProject] = useState('');
  const [transcript, setTranscript] = useState<string | null>(null);
  const r = q.data;
  const when = `${dt(m.created_at).toFormat('ccc d LLL')}, ${clock(dt(m.created_at))}`;
  const makeTask = async () => {
    if (taskFor === null || !project) return;
    try { queryClient.setQueryData(['recording', id], await api.ai.actionToTask(id, taskFor, project)); setTaskFor(null); toast('Task created', 'success'); } catch (e) { toast(errorMessage(e), 'error'); }
  };
  const openTranscript = async () => { try { setTranscript((await api.ai.transcript(id)).text); } catch (e) { toast(errorMessage(e), 'error'); } };
  const iWasRecorded = r?.consents.some(c => c.user_id === userId && c.decision === 'granted');
  const mins = r?.duration_seconds ? Math.max(1, Math.round(r.duration_seconds / 60)) : null;

  return (
    <div id={`m-${m.id}`} className="mx-5 my-2 rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900" data-testid="meeting-notes">
      <div className="mb-2 flex items-center gap-2">
        <span className="rounded-lg bg-accent-soft p-1.5 text-accent-fg"><NotebookPen className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">Meeting notes</div>
          <div className="text-xs text-slate-500">Call on {when}{mins ? ` · ${mins} min` : ''}{r ? ` · ${r.consents.filter(c => c.decision === 'granted').map(c => c.display_name.split(' ')[0]).join(', ')}` : ''}{r?.mode === 'basic' ? ' · basic (no AI summary)' : ''}</div>
        </div>
      </div>
      {r ? <Bullets text={r.summary ?? ''} /> : <Loader2 className="h-4 w-4 animate-spin text-slate-500" />}
      {r && r.action_items.length > 0 && (
        <div className="mt-3">
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Action items</div>
          <ul className="space-y-1.5">
            {r.action_items.map((a, i) => (
              <li key={i} className="flex items-center gap-2 text-sm">
                {a.task_id ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-700" /> : <span className="h-4 w-4 shrink-0 rounded border border-slate-300" />}
                <span className="min-w-0 flex-1">{a.text}{a.owner_name && <span className="text-slate-500"> – {a.owner_name}</span>}{a.due && <span className="text-slate-500"> · by {ukShortDate(a.due)}</span>}</span>
                {' '}
                {/* Kept out of copied text (select-none) and separated, so copying an item never reads "by 16 OctMake a task". */}
                {a.task_id ? <span className="shrink-0 select-none text-xs text-emerald-700">Task created</span>
                  : <Button size="sm" variant="secondary" className="shrink-0 select-none" aria-label={`Make a task: ${a.text}`} data-testid="make-task"
                    onClick={() => { setTaskFor(i); setProject(projects.data?.projects[0]?.id ?? ''); }}>Make a task</Button>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {r && (
        <div className="mt-3 flex items-center gap-3 border-t border-slate-100 pt-2 text-xs text-slate-500 dark:border-slate-800">
          {r.transcript_available && iWasRecorded && <button className="flex items-center gap-1 font-semibold text-accent-fg hover:underline" onClick={() => void openTranscript()}><FileText className="h-3.5 w-3.5" />Transcript</button>}
          {r.transcript_delete_after && <span title={`${fullDateTime(r.transcript_delete_after)}. ${RETENTION_NOTE}`} data-testid="transcript-retention">{whenWording(r.transcript_delete_after, 'Transcript will be deleted on', 'Transcript deleted on')}</span>}
          <span className="ml-auto">AI-generated – check before acting on it</span>
        </div>
      )}
      {taskFor !== null && r && (
        <Modal open onClose={() => setTaskFor(null)} title="Create a task from this action" footer={<><Button variant="ghost" onClick={() => setTaskFor(null)}>Cancel</Button><Button onClick={() => void makeTask()} disabled={!project}>Create task</Button></>}>
          <p className="mb-3 text-sm">{r.action_items[taskFor].text}</p>
          <Select value={project} onChange={e => setProject(e.target.value)} className="w-full">{(projects.data?.projects ?? []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</Select>
        </Modal>
      )}
      {transcript !== null && <Modal open wide onClose={() => setTranscript(null)} title="Transcript"><pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap font-sans text-sm">{transcript}</pre></Modal>}
    </div>
  );
}
